import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import type {
  AccountDeletionReason,
  AcquisitionSource,
  WorkArea,
} from '@this+that/models';

interface DeleteMeData {
  deleteMe: boolean;
}

export interface AccountDeletionFeedbackInput {
  reasons: AccountDeletionReason[];
  otherReason?: string;
}

interface CacheUserData {
  cacheUser: boolean;
}

const DELETE_ME = gql`
  mutation DeleteMe(
    $userId: String!
    $feedback: AccountDeletionFeedbackInput
  ) {
    deleteMe(userId: $userId, feedback: $feedback)
  }
`;

const CACHE_USER = gql`
  mutation CacheUser($userId: String!) {
    cacheUser(userId: $userId)
  }
`;

export function useDeleteMe() {
  const [deleteMeMutation, { loading, error }] =
    useMutation<DeleteMeData>(DELETE_ME);
  const deleteMe = async (
    userId: string,
    feedback?: AccountDeletionFeedbackInput,
  ) => {
    const { data } = await deleteMeMutation({
      variables: { userId, feedback },
    });
    return data?.deleteMe;
  };

  return { deleteMe, loading, error };
}

export function useCacheUser() {
  const [cacheUserMutation, { loading, error }] =
    useMutation<CacheUserData>(CACHE_USER);
  const cacheUser = async (userId: string) => {
    const { data } = await cacheUserMutation({ variables: { userId } });
    return data?.cacheUser;
  };

  return { cacheUser, loading, error };
}

interface UpdateUserUtmVariables {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  firstTouchUtmSource?: string;
}

interface UpdateUserUtmData {
  updateUserUtm: boolean;
}

const UPDATE_USER_UTM = gql`
  mutation UpdateUserUtm(
    $utmSource: String
    $utmMedium: String
    $utmCampaign: String
    $firstTouchUtmSource: String
  ) {
    updateUserUtm(
      utmSource: $utmSource
      utmMedium: $utmMedium
      utmCampaign: $utmCampaign
      firstTouchUtmSource: $firstTouchUtmSource
    )
  }
`;

export function useUpdateUserUtm() {
  const [updateUserUtmMutation, { loading, error }] = useMutation<
    UpdateUserUtmData,
    UpdateUserUtmVariables
  >(UPDATE_USER_UTM);

  const updateUserUtm = async (variables: UpdateUserUtmVariables) => {
    const { data } = await updateUserUtmMutation({ variables });
    return data?.updateUserUtm;
  };

  return { updateUserUtm, loading, error };
}

export interface UserPreferences {
  defaultTaskGroupId: string | null;
  workAreas: WorkArea[];
  isConsultant: boolean;
}

interface GetUserPreferencesData {
  getUserPreferences: UserPreferences | null;
}

const GET_USER_PREFERENCES = gql`
  query GetUserPreferences {
    getUserPreferences {
      defaultTaskGroupId
      workAreas
      isConsultant
    }
  }
`;

interface UseGetUserPreferencesOptions {
  skip?: boolean;
  pollInterval?: number;
}

export function useGetUserPreferences(options?: UseGetUserPreferencesOptions) {
  const { data, loading, error } = useQuery<GetUserPreferencesData>(
    GET_USER_PREFERENCES,
    { skip: options?.skip, pollInterval: options?.pollInterval },
  );

  return {
    userPreferences: data?.getUserPreferences ?? null,
    loading,
    error,
  };
}

interface UpdateUserWorkAreasVariables {
  workAreas: WorkArea[];
}

interface UpdateUserWorkAreasData {
  updateUserWorkAreas: boolean;
}

const UPDATE_USER_WORK_AREAS = gql`
  mutation UpdateUserWorkAreas($workAreas: [WorkArea!]!) {
    updateUserWorkAreas(workAreas: $workAreas)
  }
`;

export function useUpdateUserWorkAreas() {
  const [updateUserWorkAreasMutation, { loading, error }] = useMutation<
    UpdateUserWorkAreasData,
    UpdateUserWorkAreasVariables
  >(UPDATE_USER_WORK_AREAS);

  const updateUserWorkAreas = useCallback(
    async (workAreas: WorkArea[]) => {
      const { data } = await updateUserWorkAreasMutation({
        variables: { workAreas },
      });
      return data?.updateUserWorkAreas;
    },
    [updateUserWorkAreasMutation],
  );

  return { updateUserWorkAreas, loading, error };
}

interface UpdateUserAcquisitionSourceVariables {
  source: AcquisitionSource;
}

interface UpdateUserAcquisitionSourceData {
  updateUserAcquisitionSource: boolean;
}

const UPDATE_USER_ACQUISITION_SOURCE = gql`
  mutation UpdateUserAcquisitionSource($source: AcquisitionSource!) {
    updateUserAcquisitionSource(source: $source)
  }
`;

export function useUpdateUserAcquisitionSource() {
  const [updateUserAcquisitionSourceMutation, { loading, error }] = useMutation<
    UpdateUserAcquisitionSourceData,
    UpdateUserAcquisitionSourceVariables
  >(UPDATE_USER_ACQUISITION_SOURCE);

  const updateUserAcquisitionSource = useCallback(
    async (source: AcquisitionSource) => {
      const { data } = await updateUserAcquisitionSourceMutation({
        variables: { source },
      });
      return data?.updateUserAcquisitionSource;
    },
    [updateUserAcquisitionSourceMutation],
  );

  return { updateUserAcquisitionSource, loading, error };
}
